import {
  collection, deleteField, doc, getDocs, runTransaction,
} from "firebase/firestore";
import type { SubmittedDocument } from "~/types/SubmittedDocument";
import type { Revision } from "~/types/Revision";
import { DocumentState } from "~/types/enums/DocumentState";

const hasOriginFile = (document: SubmittedDocument, revisions: Revision[]) =>
  !!(document.googleDriveFileId || document.originalFileMediaHref || document.fileType ||
    revisions.some(revision => revision.fileObject || revision.fileObjects?.length));

// Teacher deletion withdraws portal writing without discarding the student's text.
export async function useDeleteTeacherSubmission(documentId: string) {
  const db = useFirestore();
  const documentRef = doc(db, `/documents/${documentId}`);
  // No orderBy: legacy revisions without timestamps must also be inspected.
  const revisions = await getDocs(collection(db, `/documents/${documentId}/revisions`));

  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(documentRef);
    if (!snapshot.exists()) throw new Error("Submission no longer exists.");
    const document = snapshot.data() as SubmittedDocument;
    if (document.state === DocumentState.archived) return "archived";

    // Read current revision contents on retries, before making any writes.
    const snapshots = await Promise.all(revisions.docs.map(revision => transaction.get(revision.ref)));
    const activeRevisions = snapshots.filter(revision => revision.exists() && revision.data().isArchived !== true);
    const draft = activeRevisions.find(revision => revision.data().studentSubmissionOps != null);
    const uploaded = hasOriginFile(document, snapshots.filter(revision => revision.exists()).map(revision => revision.data() as Revision));

    if (!draft || uploaded) {
      transaction.update(documentRef, {
        state: DocumentState.archived,
        stateBeforeArchive: document.state,
        archivedAtTimestamp: Date.now(),
        lastUpdatedTimestamp: Date.now(),
      });
      return "archived";
    }

    for (const revision of activeRevisions) {
      if (revision.data().studentSubmissionOps == null) {
        transaction.update(revision.ref, { isArchived: true });
      }
    }
    transaction.update(documentRef, {
      state: DocumentState.editing,
      lastUpdatedTimestamp: Date.now(),
      startGradingAtTimestamp: deleteField(),
      finishedGradingAtTimestamp: deleteField(),
      earnedPoints: deleteField(),
      maxPoints: deleteField(),
      documentGradedState: deleteField(),
      isReturnedToStudent: deleteField(),
      submittedAtTimestamp: deleteField(),
      submittedAtTimestampMillis: deleteField(),
      studentGuidedReviewStepIndex: deleteField(),
      submissionSeal: deleteField(),
      badge: deleteField(),
      archivedAtTimestamp: deleteField(),
      stateBeforeArchive: deleteField(),
      isLate: false,
    });
    return "draft";
  });
}
